/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, Avatar } from '@components/atoms'
import { Modal, ModalInfoType } from '@components/molecules'
import { Footer } from '@components/systems'
import { MonolithAccountNav } from '@components/systems/monolith/MonolithAccountNav'
import { AvatarUploadModal } from '@components/systems/monolith/avatar/AvatarUploadModal'
import { ChangePasswordModal } from '@components/systems/monolith/account/ChangePasswordModal'
import { MonolithConfirmModal } from '@components/systems/monolith/MonolithConfirmModal'
import { mono } from '@components/systems/monolith/monolith.tokens'
import { customerAccountStyle } from './customerAccount.style'

import { useAppSelector, useAppDispatch, customerAuthActions } from '@redux/index'
import { deepPurple } from '@mui/material/colors'
import { CustomerType } from '@schemas'
import { produce } from 'immer'
import { useLocation, useNavigate } from 'react-router-dom'

/** Change Password 모달의 주소 — My Info 위에 연다. 옛 /customer/change-password 는 여기로 넘긴다 */
const PASSWORD_PATH = '/customer/info/password'

/**
 * My Info — Monolith(2안).
 *
 * 보기 모드는 스펙 테이블(§5), 편집 모드는 밑줄 입력. 이메일 인증(코드 발송·검증)과
 * 저장 흐름은 구 페이지의 redux(customerAuth) 를 그대로 쓴다 — 상태 이름·모달 타깃 동일.
 *
 * 2026-10-06 Change Password · Log out 을 계정 네비에서 이 페이지 안으로 옮겼다(사용자 지시).
 * Change Password = 버튼 → 전체 화면 모달(`/customer/info/password`), Log out = 버튼 → 확인 모달.
 */
const MonolithCustomerInfoPage = () => {
	// hook
	const location = useLocation()
	const navigate = useNavigate()

	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const reduxCustomer: CustomerType = useAppSelector(state => state.customerAuth.customer)
	const isEditCustomerInfo: boolean = useAppSelector(
		state => state.customerAuth.isEditCustomerInfo,
	)
	const updateCustomerErrorMessage: string = useAppSelector(
		state => state.customerAuth.updateCustomerErrorMessage,
	)
	const modalInfo: ModalInfoType = useAppSelector(
		state => state.customerAuth.customerInfoModalInfo,
	)
	const showCustomerInfoModal: boolean = useAppSelector(
		state => state.customerAuth.showCustomerInfoModal,
	)
	const showCustomerPasswordModal: boolean = useAppSelector(
		state => state.customerAuth.showCustomerPasswordModal,
	)

	const isPasswordOpen: boolean = location.pathname === PASSWORD_PATH

	// state
	const [customer, setCustomer] = React.useState({
		email: '',
		name: '',
		confirmEmailNewsYn: 0,
	})
	const [isEmailVerification, setIsEmailVerification] = React.useState<boolean>(false)
	const [emailVerificationCode, setEmailVerificationCode] = React.useState<string>('')
	const [isLogoutOpen, setIsLogoutOpen] = React.useState<boolean>(false)

	React.useEffect(() => {
		setCustomer({
			name: reduxCustomer.name,
			email: reduxCustomer.email,
			confirmEmailNewsYn: reduxCustomer.confirmEmailNewsYn,
		})
	}, [reduxCustomer])

	const update = (patch: Partial<typeof customer>) => {
		const newCustomer = produce(customer, draft => {
			Object.assign(draft, patch)
		})

		setCustomer(newCustomer)
	}

	const onVerificationCodeSubmit = () => {
		reduxDispatch(customerAuthActions.VERIFICATION_EMAIL({ code: emailVerificationCode }))
	}

	const onModalSubmit = () => {
		if (modalInfo.target === 'emailVerification') {
			reduxDispatch(customerAuthActions.TRIGGER_SEND_EMAIL_VERIFICATION_CODE(true))
			setIsEmailVerification(true)
		}
	}

	const onSave = () => {
		// 이메일을 바꾸면 인증이 풀린다 — 저장과 동시에 인증 코드 입력을 열어 둔다.
		// (코드 발송은 saga 의 updateCustomerSaga 가 이어서 처리한다)
		const isEmailChanged: boolean = customer.email !== reduxCustomer.email

		reduxDispatch(customerAuthActions.UPDATE_CUSTOMER(customer))

		if (isEmailChanged) {
			setIsEmailVerification(true)
		}
	}

	return (
		<View css={style}>
			<View className="mnWrap">
				<MonolithAccountNav />

				<View className="mnAccountBody">
					{isEditCustomerInfo ? (
						<>
							<View className="mnField">
								<label className="mnLabel" htmlFor="mnInfoName">
									Name
								</label>
								<input
									id="mnInfoName"
									type="text"
									className="mnInput"
									value={customer.name}
									onChange={e => update({ name: e.target.value })}
									autoComplete="name"
								/>
							</View>

							<View className="mnField">
								<label className="mnLabel" htmlFor="mnInfoEmail">
									Email
								</label>
								<input
									id="mnInfoEmail"
									type="email"
									className="mnInput"
									value={customer.email}
									onChange={e => update({ email: e.target.value })}
									autoComplete="email"
								/>

								<button
									type="button"
									className="mnCheck"
									aria-pressed={customer.confirmEmailNewsYn === 1}
									onClick={() =>
										update({
											confirmEmailNewsYn: customer.confirmEmailNewsYn === 1 ? 0 : 1,
										})
									}
								>
									<span
										className={`mnCheckBox ${
											customer.confirmEmailNewsYn === 1 ? 'on' : ''
										}`}
										aria-hidden="true"
									/>
									<span className="mnCheckLabel">
										Yes, I’d like to receive updates on the latest guitar gear and
										discounts.
									</span>
								</button>
							</View>
						</>
					) : (
						<View className="mnInfoTable">
							<View className="mnSpecRow">
								<span className="mnSpecLabel">Avatar</span>
								<View className="mnSpecValue mnInfoAvatarValue">
									<Avatar
										className="mnInfoAvatar"
										picture={reduxCustomer.profileImageUrl}
										name={reduxCustomer.name}
										color={
											reduxCustomer.profileColor === ''
												? mono.ink
												: reduxCustomer.profileColor
										}
										size={64}
										extpandingDisable={true}
									/>
									<button
										type="button"
										className="mnTextLink mnInfoAvatarChange"
										onClick={() =>
											reduxDispatch(customerAuthActions.SHOW_PROFILE_IMAGE_MODAL(true))
										}
									>
										{reduxCustomer.profileImageUrl === '' ? 'Add picture' : 'Change'}
									</button>
								</View>
							</View>

							<View className="mnSpecRow">
								<span className="mnSpecLabel">Name</span>
								<span className="mnSpecValue">{reduxCustomer.name}</span>
							</View>

							<View className="mnSpecRow">
								<span className="mnSpecLabel">Email</span>
								<View className="mnSpecValue mnInfoEmailValue">
									<span>{reduxCustomer.email}</span>

									{reduxCustomer.verifiedYn === 1 ? (
										<span className="mnMeta">Verified</span>
									) : (
										!isEmailVerification && (
											<button
												type="button"
												className="mnTextLink mnInfoVerify"
												onClick={() => {
													reduxDispatch(
														customerAuthActions.CUSTOMER_INFO_MODAL_INFO({
															target: 'emailVerification',
															title: 'Email Verification',
														}),
													)
													reduxDispatch(
														customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(true),
													)
												}}
											>
												Verify
											</button>
										)
									)}
								</View>
							</View>

							<View className="mnSpecRow">
								<span className="mnSpecLabel">Newsletter</span>
								<span className="mnSpecValue">
									{reduxCustomer.confirmEmailNewsYn === 1 ? 'Subscribed' : 'Off'}
								</span>
							</View>
						</View>
					)}

					{reduxCustomer.verifiedYn === 0 && isEmailVerification && (
						<View className="mnInfoVerifyBlock">
							<View className="mnField">
								<label className="mnLabel" htmlFor="mnInfoCode">
									Email verification code
								</label>
								<input
									id="mnInfoCode"
									type="text"
									className="mnInput"
									value={emailVerificationCode}
									onChange={e => setEmailVerificationCode(e.target.value)}
									autoComplete="one-time-code"
								/>
							</View>

							<View className="mnAccountActions">
								<button
									type="button"
									className="mnCta"
									disabled={emailVerificationCode === ''}
									onClick={onVerificationCodeSubmit}
								>
									Verify
								</button>
								<button
									type="button"
									className="mnTextLink"
									onClick={() => setIsEmailVerification(false)}
								>
									Cancel
								</button>
							</View>
						</View>
					)}

					{reduxCustomer.verifiedYn === 0 && !isEmailVerification && (
						<p className="mnInfoWarning">
							Until this email is verified we cannot send order confirmations, shipping
							notices or password resets to it.
						</p>
					)}

					{!isEmailVerification && (
						<>
							{updateCustomerErrorMessage !== '' && (
								<span className="mnError">{updateCustomerErrorMessage}</span>
							)}

							{isEditCustomerInfo ? (
								<View className="mnAccountActions">
									<button type="button" className="mnCta" onClick={onSave}>
										Save
									</button>
									<button
										type="button"
										className="mnTextLink"
										onClick={() =>
											reduxDispatch(customerAuthActions.IS_EDIT_CUSTOMER_INFO(false))
										}
									>
										Cancel
									</button>
								</View>
							) : (
								<>
									<View className="mnAccountActions">
										<button
											type="button"
											className="mnCtaGhost"
											onClick={() =>
												reduxDispatch(customerAuthActions.IS_EDIT_CUSTOMER_INFO(true))
											}
										>
											Edit
										</button>
									</View>

									{/* Edit 아래 글자 버튼만 — Change password, 맨 아래 Log out (2026-10-06 사용자 지시) */}
									<View className="mnInfoTextActions">
										<button
											type="button"
											className="mnInfoTextAction"
											onClick={() => navigate(PASSWORD_PATH)}
										>
											Change password
										</button>
										<button
											type="button"
											className="mnInfoTextAction"
											onClick={() => setIsLogoutOpen(true)}
										>
											Log out
										</button>
									</View>
								</>
							)}
						</>
					)}
				</View>
			</View>

			<AvatarUploadModal />

			{isPasswordOpen && (
				<ChangePasswordModal
					onClose={() => navigate('/customer/info', { replace: true })}
				/>
			)}

			<MonolithConfirmModal
				isOpen={isLogoutOpen}
				title="Log out"
				body="This device is signed out and returns to the home page. Your orders stay on the account."
				confirmLabel="Log out"
				cancelLabel="Stay signed in"
				onConfirm={() => reduxDispatch(customerAuthActions.TRIGGER_LOGOUT(true))}
				onCancel={() => setIsLogoutOpen(false)}
			/>

			<Modal
				target="passwordUpdated"
				title="Password Update Success"
				show={showCustomerPasswordModal}
				onCloseModal={() =>
					reduxDispatch(customerAuthActions.SHOW_CUSTOMER_PASSWORD_MODAL(false))
				}
				closeStatus="inherit"
				showSubmit={false}
				closeTitle="Close"
			>
				<View css={{ width: '30rem', maxWidth: '80vw' }}>
					<Text category="p2">Your password has been successfully updated.</Text>
				</View>
			</Modal>

			<Footer />

			<Modal
				target={modalInfo.target ?? ''}
				title={modalInfo.title}
				show={showCustomerInfoModal}
				onCloseModal={() => {
					reduxDispatch(customerAuthActions.SHOW_CUSTOMER_INFO_MODAL(false))

					if (modalInfo.target === 'verificationSuccess') {
						setIsEmailVerification(false)
					}
				}}
				closeStatus="inherit"
				showSubmit={modalInfo.target === 'emailVerification'}
				closeTitle={modalInfo.target === 'updateSuccess' ? 'Close' : 'Cancel'}
				submitTitle="Get Code"
				submitIconColor={deepPurple['A400']}
				isTitleDanger={modalInfo.target === 'verificationFailed'}
				onSubmit={onModalSubmit}
			>
				{modalInfo.target === 'updateSuccess' ? (
					<View css={{ width: '30rem', maxWidth: '80vw' }}>
						<Text category="p2">Your information has been successfully updated.</Text>
					</View>
				) : modalInfo.target === 'emailVerification' ? (
					<View>
						<Text category="p2">
							{"To verify this email, please click the 'Get Code' button below."}
						</Text>
					</View>
				) : modalInfo.target === 'verificationCodeSent' ? (
					<View>
						<Text category="p2">{"'Verification code' has been sent."}</Text>
						<Text category="p2">Please check your registered email.</Text>
					</View>
				) : modalInfo.target === 'verificationSuccess' ? (
					<View>
						<Text category="p2">Verification has been success.</Text>
					</View>
				) : modalInfo.target === 'verificationFailed' ? (
					<View>
						<Text category="p2">Please check your code again.</Text>
					</View>
				) : null}
			</Modal>
		</View>
	)
}

export default MonolithCustomerInfoPage

const style = css({
	...customerAccountStyle,

	'.mnInfoTable': {
		width: '100%',
		borderTop: `1px solid ${mono.line}`,

		/**
		 * 첫 행은 본인 아바타. 사진 업로드가 없는 계정이라 이니셜 원이 곧 아바타이고,
		 * 색은 두지 않는다(§2 액센트 없음) — 잉크 원 + 흰 이니셜.
		 */
		'.mnInfoAvatarValue': {
			display: 'flex',
			alignItems: 'center',
			gap: 20,
			flexWrap: 'wrap',
		},

		'.mnInfoAvatar': {
			margin: '4px 0',
		},

		'.mnInfoAvatarChange': {
			fontSize: 13,
		},

		'.mnInfoEmailValue': {
			display: 'flex',
			alignItems: 'center',
			gap: 20,
			flexWrap: 'wrap',
		},

		'.mnInfoVerify': {
			fontSize: 13,
		},
	},

	/**
	 * Edit 아래 글자 버튼 줄 — Edit 와 같은 왼쪽 선 · 같은 글자(14/500 ink), 테두리 없음.
	 * 한 버튼이 44 높이라 위아래 간격은 그 여백이 만든다. 호버 = dim
	 */
	'.mnInfoTextActions': {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'flex-start',
		marginTop: -12,

		'.mnInfoTextAction': {
			display: 'inline-flex',
			alignItems: 'center',
			minHeight: 44,
			padding: 0,
			border: 0,
			background: 'transparent',
			fontFamily: 'inherit',
			fontSize: 14,
			fontWeight: 500,
			color: mono.ink,
			cursor: 'pointer',
			transition: 'color 200ms ease-out',
			'&:hover': { color: mono.dim },
			'&:focus-visible': { outline: `1px solid ${mono.ink}`, outlineOffset: 2 },
			'@media (prefers-reduced-motion: reduce)': { transition: 'none' },
		},
	},

	'.mnInfoVerifyBlock': {
		display: 'flex',
		flexDirection: 'column',
		gap: 28,
		maxWidth: 420,
	},

	'.mnInfoWarning': {
		fontSize: 15,
		lineHeight: 1.7,
		fontWeight: 300,
		color: mono.dim,
		maxWidth: 520,
	},

	'.mnCta:disabled': {
		background: mono.lineSoft,
		color: '#FFFFFF',
		cursor: 'default',
	},
})
